import React from 'react';
import { TrendingUp, TrendingDown, ArrowUpRight, Star, ChevronRight } from 'lucide-react';
import { marketData, opportunityScore } from '../../../data/terminalData';

interface HeroPanelProps {
  isExpanded?: boolean;
}

export const HeroPanel: React.FC<HeroPanelProps> = ({ isExpanded = false }) => {
  const { fdi, macro, ratings } = marketData;
  const score = opportunityScore;

  return (
    <div className="bg-terminal-panel border border-terminal-border rounded-lg overflow-hidden">
      {/* Main Layout - Hero Metrics + NATEX Score */}
      <div className="flex flex-col lg:flex-row">
        {/* Left: Hero Metrics Grid */}
        <div className="flex-1 p-5 lg:p-6">
          {/* Country Header */}
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-data-amber to-data-green flex items-center justify-center">
              <span className="text-black font-black text-lg">UZ</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-wide">UZBEKISTAN</h1>
              <p className="text-sm text-gray-400">Central Asia's Rising Economic Power</p>
            </div>
          </div>

          {/* Hero Metrics Row - 4 Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* GDP */}
            <div className="bg-terminal-bg/60 rounded-xl p-4 border border-terminal-border/50">
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">GDP 2025</div>
              <div className="text-3xl lg:text-4xl font-mono font-black text-white">
                ${macro.gdp.value}B
              </div>
              <div className="flex items-center gap-1.5 mt-2">
                <ArrowUpRight size={14} className="text-data-green" />
                <span className="text-sm text-data-green font-semibold">+{macro.gdp.yoy}%</span>
                <span className="text-xs text-gray-500">YoY</span>
              </div>
              <div className="text-xs text-gray-600 mt-1">World Bank</div>
            </div>

            {/* Reserves */}
            <div className="bg-terminal-bg/60 rounded-xl p-4 border border-terminal-border/50">
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Reserves</div>
              <div className="text-3xl lg:text-4xl font-mono font-black text-data-amber">
                ${macro.fxReserves.value}B
              </div>
              <div className="flex items-center gap-1.5 mt-2">
                <ArrowUpRight size={14} className="text-data-green" />
                <span className="text-sm text-data-green font-semibold">+21%</span>
                <span className="text-xs text-gray-500">YoY</span>
              </div>
              <div className="text-xs text-gray-600 mt-1">CBU</div>
            </div>

            {/* Growth */}
            <div className="bg-terminal-bg/60 rounded-xl p-4 border border-terminal-border/50">
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Growth</div>
              <div className="text-3xl lg:text-4xl font-mono font-black text-data-green">
                {macro.gdpGrowth.value}%
              </div>
              <div className="flex items-center gap-1.5 mt-2">
                <TrendingUp size={14} className="text-data-green" />
                <span className="text-sm text-white">9M 2025</span>
              </div>
              <div className="text-xs text-gray-600 mt-1">Stat.uz</div>
            </div>

            {/* Inflation */}
            <div className="bg-terminal-bg/60 rounded-xl p-4 border border-terminal-border/50">
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Inflation</div>
              <div className="text-3xl lg:text-4xl font-mono font-black text-data-amber">
                {macro.inflation.value}%
              </div>
              <div className="flex items-center gap-1.5 mt-2">
                <TrendingDown size={14} className="text-data-green" />
                <span className="text-sm text-data-green">Falling</span>
              </div>
              <div className="text-xs text-gray-600 mt-1">CBU</div>
            </div>
          </div>
        </div>

        {/* Right: NATEX Score Hero */}
        <div className="lg:w-72 xl:w-80 bg-gradient-to-br from-terminal-bg to-terminal-panel border-t lg:border-t-0 lg:border-l border-terminal-border p-5 lg:p-6 flex flex-col items-center justify-center">
          <div className="text-xs text-data-amber uppercase tracking-widest font-semibold mb-3">
            NATEX Opportunity Score
          </div>

          {/* Giant Score */}
          <div className="flex items-baseline gap-1">
            <span className="text-7xl lg:text-8xl font-mono font-black text-data-amber leading-none">
              {score.overall}
            </span>
            <span className="text-2xl lg:text-3xl text-gray-500 font-mono">/100</span>
          </div>

          {/* Signal Badge */}
          <div className="mt-4 px-4 py-2 bg-data-green/20 rounded-lg border border-data-green/30">
            <div className="flex items-center gap-2">
              <Star size={16} className="text-data-green fill-data-green" />
              <span className="text-base font-bold text-data-green">{score.signal}</span>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="mt-4 w-full grid grid-cols-2 gap-2">
            <div className="text-center p-2 bg-terminal-border/30 rounded">
              <div className="text-lg font-bold text-white">+5 pts</div>
              <div className="text-xs text-gray-500">vs Q3 2024</div>
            </div>
            <div className="text-center p-2 bg-terminal-border/30 rounded">
              <div className="text-lg font-bold text-data-green">#1</div>
              <div className="text-xs text-gray-500">Central Asia</div>
            </div>
          </div>

          {/* View Full Breakdown Link */}
          <button className="mt-4 flex items-center gap-1 text-xs text-data-amber hover:text-white transition-colors">
            <span>View Full Breakdown</span>
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Bottom Bar - Key Highlights */}
      <div className="bg-terminal-bg/50 border-t border-terminal-border px-5 py-3 flex items-center justify-between gap-6 overflow-x-auto">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-data-green font-bold">#1</span>
            <span className="text-sm text-gray-400">FDI Growth in Central Asia</span>
          </div>
          <div className="w-px h-4 bg-terminal-border hidden lg:block" />
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-data-amber font-bold">$11.9B</span>
            <span className="text-sm text-gray-400">Record FDI 2024</span>
          </div>
          <div className="w-px h-4 bg-terminal-border hidden lg:block" />
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-data-green font-bold">BB</span>
            <span className="text-sm text-gray-400">Credit Rating (2x Upgraded)</span>
          </div>
          <div className="w-px h-4 bg-terminal-border hidden lg:block" />
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-data-amber font-bold">Mar 2026</span>
            <span className="text-sm text-gray-400">WTO Accession Target</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroPanel;
